import {
  FREE_HABIT_LIMIT,
  USAGE_LIMITS,
  type UsageLimitError,
} from '@goaltree/backend/convex/lib/usage'
import i18next from 'i18next'
import { toast } from 'sonner'
import { create } from 'zustand'

export function getUsageLimit(err: unknown): UsageLimitError | null {
  const data = (err as { data?: { kind?: string } } | null)?.data
  return data?.kind === 'usageLimit' ? (data as UsageLimitError) : null
}

export const useUsageLimitStore = create<{
  hit: UsageLimitError | null
  set: (hit: UsageLimitError | null) => void
}>((set) => ({ hit: null, set: (hit) => set({ hit }) }))

// Signed-out, free and trial caps open the sign-up / upgrade prompt; Pro caps
// get a friendly "resets" toast. Returns false when `err` isn't a usage-limit
// error so callers keep their own error handling.
export function handleUsageLimit(err: unknown): boolean {
  const hit = getUsageLimit(err)
  if (!hit) return false
  if (hit.plan !== 'pro') useUsageLimitStore.getState().set(hit)
  else
    toast(
      i18next.t(
        USAGE_LIMITS[hit.feature].pro.period === 'day'
          ? 'usageLimit.proDaily'
          : 'usageLimit.proMonthly',
      ),
    )
  return true
}

export const isHabitLimit = (err: unknown) => getUsageLimit(err)?.feature === 'habits'

// Opens the habit-cap upgrade prompt without an error (composer lock, held-back plan habits).
export function openHabitUpsell() {
  useUsageLimitStore
    .getState()
    .set({ kind: 'usageLimit', feature: 'habits', plan: 'free', limit: FREE_HABIT_LIMIT })
}

const ANON_ID_KEY = 'goaltree.anonId'
let sessionAnonId: string | undefined

// Lets the backend cap signed-out plan builds per visitor (ignored once signed
// in); locale + zone let it tailor plans to the user's country and date.
export function planUsageArgs() {
  let anonId: string
  try {
    anonId = localStorage.getItem(ANON_ID_KEY) ?? crypto.randomUUID()
    localStorage.setItem(ANON_ID_KEY, anonId)
  } catch {
    anonId = sessionAnonId ??= crypto.randomUUID()
  }
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return {
    anonId,
    tzOffsetMin: new Date().getTimezoneOffset(),
    ...(navigator.language && { deviceLocale: navigator.language }),
    ...(timeZone && { timeZone }),
  }
}
